import { isRecord } from './util/type-guards.ts';

export interface LiveBrowserTranscript {
  sessionId: string;
  eventId: string;
  speaker: 'user' | 'assistant';
  delta: string;
  startMs: number;
  endMs: number;
}

export interface BrowserLiveMediaEvents {
  transcript(fragment: LiveBrowserTranscript): void;
  failed(message: string, retryable: boolean): void;
}

/** Native media only. The application owns authorization, signaling, activation and retries. */
export class BrowserLiveMedia {
  readonly #events: BrowserLiveMediaEvents;
  #microphone: MediaStream | null = null;
  #peer: RTCPeerConnection | null = null;
  #channel: RTCDataChannel | null = null;
  #audio: HTMLAudioElement | null = null;
  #context: AudioContext | null = null;
  #input: AnalyserNode | null = null;
  #sources: MediaStreamAudioSourceNode[] = [];
  #output: AnalyserNode | null = null;
  #muted = false;
  #active = false;
  #generation = 0;
  #sessionId = '';
  #seen = new Set<string>();
  #disconnectTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(events: BrowserLiveMediaEvents) {
    this.#events = events;
  }

  async createOffer(): Promise<string> {
    this.disconnect();
    const generation = this.#generation;
    if (!this.#microphone) {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      if (generation !== this.#generation) {
        for (const track of stream.getTracks()) track.stop();
        throw new Error('Voice setup was cancelled');
      }
      this.#microphone = stream;
    }
    this.#setCapture();
    this.#context ??= new AudioContext();
    await this.#context.resume();
    if (generation !== this.#generation) throw new Error('Voice setup was cancelled');
    this.#input = this.#analyser(this.#microphone);
    const peer = new RTCPeerConnection();
    this.#peer = peer;
    const audio = new Audio();
    audio.autoplay = true;
    this.#audio = audio;
    for (const track of this.#microphone.getTracks()) {
      peer.addTrack(track, this.#microphone);
    }
    peer.ontrack = (event) => {
      if (this.#peer !== peer || !this.#context) return;
      const stream = new MediaStream([event.track]);
      audio.srcObject = stream;
      this.#output = this.#analyser(stream);
      void audio.play().catch(() => {
        if (this.#peer === peer)
          this.#events.failed('Voice playback needs permission. Please start voice again.', false);
      });
    };
    peer.onconnectionstatechange = () => this.#connectionChanged(peer);
    const channel = peer.createDataChannel('oai-events');
    this.#channel = channel;
    channel.onclose = () => {
      if (this.#peer === peer) this.#events.failed('Voice captions disconnected', true);
    };
    channel.onmessage = (event) => {
      if (this.#peer === peer && typeof event.data === 'string') this.#caption(event.data);
    };
    await peer.setLocalDescription(await peer.createOffer());
    await this.#gather(peer);
    if (this.#peer !== peer || !peer.localDescription?.sdp)
      throw new Error('Voice setup was cancelled');
    return peer.localDescription.sdp;
  }

  async acceptAnswer(sessionId: string, sdp: string): Promise<void> {
    const peer = this.#peer;
    const channel = this.#channel;
    if (!peer || !channel) throw new Error('Voice media is not prepared');
    this.#sessionId = sessionId;
    await peer.setRemoteDescription({ type: 'answer', sdp });
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => finish(new Error('Voice media connection timed out')), 15_000);
      const changed = () => {
        if (
          this.#peer !== peer ||
          peer.connectionState === 'closed' ||
          peer.connectionState === 'failed' ||
          channel.readyState === 'closed'
        ) {
          finish(new Error('Voice media connection failed'));
        } else if (peer.connectionState === 'connected' && channel.readyState === 'open') finish();
      };
      const finish = (error?: Error) => {
        clearTimeout(timer);
        peer.removeEventListener('connectionstatechange', changed);
        channel.removeEventListener('open', changed);
        channel.removeEventListener('close', changed);
        if (error) reject(error);
        else resolve();
      };
      peer.addEventListener('connectionstatechange', changed);
      channel.addEventListener('open', changed);
      channel.addEventListener('close', changed);
      changed();
    });
  }

  activate(): void {
    this.#active = true;
    this.#setCapture();
  }

  setMuted(muted: boolean): void {
    this.#muted = muted;
    this.#setCapture();
  }

  get inputLevel(): number {
    return this.#active && !this.#muted ? this.#level(this.#input) : 0;
  }

  get outputLevel(): number {
    return this.#level(this.#output);
  }

  /** Ends one provider attempt, retaining the user's microphone permission for replacement. */
  disconnect(): void {
    this.#generation++;
    this.#active = false;
    this.#setCapture();
    clearTimeout(this.#disconnectTimer);
    const peer = this.#peer;
    this.#peer = null;
    this.#channel?.close();
    this.#channel = null;
    peer?.close();
    if (this.#audio) {
      this.#audio.pause();
      this.#audio.srcObject = null;
      this.#audio = null;
    }
    for (const source of this.#sources) source.disconnect();
    this.#sources = [];
    this.#input?.disconnect();
    this.#output?.disconnect();
    this.#input = null;
    this.#output = null;
    this.#sessionId = '';
    this.#seen.clear();
  }

  /** Releases capture and playback, then waits for audio processing to close. */
  async stop(): Promise<void> {
    this.disconnect();
    for (const track of this.#microphone?.getTracks() ?? []) track.stop();
    this.#microphone = null;
    const context = this.#context;
    this.#context = null;
    await context?.close();
  }

  #setCapture(): void {
    for (const track of this.#microphone?.getAudioTracks() ?? []) {
      track.enabled = this.#active && !this.#muted;
    }
  }

  #analyser(stream: MediaStream): AnalyserNode | null {
    if (!this.#context) return null;
    const analyser = this.#context.createAnalyser();
    analyser.fftSize = 256;
    const source = this.#context.createMediaStreamSource(stream);
    this.#sources.push(source);
    source.connect(analyser);
    return analyser;
  }

  #level(analyser: AnalyserNode | null): number {
    if (!analyser) return 0;
    const samples = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(samples);
    let sum = 0;
    for (const sample of samples) sum += sample * sample;
    return Math.min(1, Math.sqrt(sum / samples.length) * 4);
  }

  #connectionChanged(peer: RTCPeerConnection): void {
    if (this.#peer !== peer) return;
    clearTimeout(this.#disconnectTimer);
    if (peer.connectionState === 'failed') {
      this.#events.failed('Voice media disconnected', true);
    } else if (peer.connectionState === 'disconnected') {
      this.#disconnectTimer = setTimeout(() => {
        if (this.#peer === peer && peer.connectionState === 'disconnected') {
          this.#events.failed('Voice media disconnected', true);
        }
      }, 3_000);
    }
  }

  #gather(peer: RTCPeerConnection): Promise<void> {
    if (peer.iceGatheringState === 'complete') return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => finish(new Error('Voice network setup timed out')), 10_000);
      const changed = () => {
        if (peer.iceGatheringState === 'complete') finish();
      };
      const finish = (error?: Error) => {
        clearTimeout(timer);
        peer.removeEventListener('icegatheringstatechange', changed);
        if (error) reject(error);
        else resolve();
      };
      peer.addEventListener('icegatheringstatechange', changed);
      changed();
    });
  }

  #caption(text: string): void {
    if (text.length > 64_000 || !this.#sessionId) return;
    let event: unknown;
    try {
      event = JSON.parse(text);
    } catch {
      return;
    }
    if (!isRecord(event)) return;
    const type = event['type'];
    if (type !== 'session.input_transcript.delta' && type !== 'session.output_transcript.delta')
      return;
    const id = event['event_id'];
    const delta = event['delta'];
    const start = event['start_ms'];
    const end = event['end_ms'];
    if (
      typeof id !== 'string' ||
      typeof delta !== 'string' ||
      typeof start !== 'number' ||
      typeof end !== 'number' ||
      !Number.isFinite(start) ||
      !Number.isFinite(end) ||
      start < 0 ||
      end < start ||
      this.#seen.has(id)
    )
      return;
    this.#seen.add(id);
    if (this.#seen.size > 100_000) {
      this.#events.failed('Voice caption limit reached', false);
      return;
    }
    this.#events.transcript({
      sessionId: this.#sessionId,
      eventId: id,
      speaker: type === 'session.input_transcript.delta' ? 'user' : 'assistant',
      delta,
      startMs: start,
      endMs: end,
    });
  }
}
